import { useEffect, useRef, useState } from 'react';
import { api, ErroDoPortal } from '../api';
import {
  Ajuda, MolduraDaEntrada, Notificacao, Passos, Seta, Telefone,
} from './MolduraDaEntrada';

/**
 * A entrada, em dois passos — na faixa da marca (onda 284, opção C).
 *
 * ── Por que não há senha ─────────────────────────────────────────────
 *
 * Porque a identidade do cliente já é o e-mail — o mesmo que a Testesolo
 * cadastrou para mandar os relatórios. Uma senha seria um segundo
 * segredo a criar, esquecer, recuperar e vazar, para provar exatamente a
 * mesma coisa que um código enviado àquela caixa já prova: que quem está
 * entrando tem acesso a ela.
 *
 * ── O que a revisão de 03/10 pediu, e onde está ──────────────────────
 *
 *   · **para qual e-mail foi o código** (P1 #5): o aviso do passo 2 repete
 *     o endereço que a pessoa digitou, com «Trocar e-mail» ao lado. Não
 *     vaza nada — o servidor continua respondendo igual para e-mail
 *     cadastrado ou não; o que aparece é o que ela mesma escreveu. Um erro
 *     de digitação passa a ser visto na hora, e não depois de esperar um
 *     e-mail que nunca chega;
 *   · **em que passo estou** (P2 #6): «Passo 1 de 2 · E-mail»;
 *   · **reenviar sem gastar o limite** (P2 #7): «Reenviar em 0:42». O
 *     limite de verdade continua no banco (`conferirRitmo`, quinze
 *     minutos); a contagem só evita que a pessoa o gaste sem perceber.
 */

/** Segundos até o «Reenviar» acordar. */
const ESPERA_DO_REENVIO = 60;

const relogio = (s: number) =>
  `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

type Aviso = { tipo: 'atencao' | 'erro'; texto: string };

export function Entrar({ aoEntrar }: { aoEntrar(): void }) {
  const [email, setEmail] = useState('');
  const [codigo, setCodigo] = useState('');
  const [fase, setFase] = useState<'email' | 'codigo'>('email');
  const [minutos, setMinutos] = useState(10);
  const [aviso, setAviso] = useState<Aviso | null>(null);
  const [erroDoCodigo, setErroDoCodigo] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [restante, setRestante] = useState(0);
  /*
   * O e-mail que levou «muitos pedidos». Enquanto o campo for ele, o
   * botão espera; trocar o endereço o libera (o limite do banco conta
   * também por e-mail, e é por e-mail que a pessoa tenta de novo).
   */
  const [travadoPara, setTravadoPara] = useState<string | null>(null);
  const campoDoCodigo = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (restante <= 0) return;
    const t = setTimeout(() => setRestante((r) => r - 1), 1000);
    return () => clearTimeout(t);
  }, [restante]);

  const endereco = email.trim();

  const pedir = async (ev?: React.FormEvent) => {
    ev?.preventDefault();
    setOcupado(true); setAviso(null); setErroDoCodigo(null);
    try {
      const r = await api<{ minutos?: number }>('/entrar', {
        metodo: 'POST', corpo: { email: endereco },
      });
      if (typeof r?.minutos === 'number') setMinutos(r.minutos);
      setCodigo('');
      setFase('codigo');
      setRestante(ESPERA_DO_REENVIO);
    } catch (e) {
      const status = e instanceof ErroDoPortal ? e.status : 0;
      if (status === 429) setTravadoPara(endereco);
      setAviso({
        tipo: status === 429 ? 'atencao' : 'erro',
        texto: e instanceof ErroDoPortal ? e.message : 'Não foi possível.',
      });
    } finally { setOcupado(false); }
  };

  const confirmar = async (ev: React.FormEvent) => {
    ev.preventDefault();
    setOcupado(true); setErroDoCodigo(null); setAviso(null);
    try {
      await api('/confirmar', {
        metodo: 'POST', corpo: { email: endereco, codigo },
      });
      aoEntrar();
    } catch (e) {
      setErroDoCodigo(e instanceof ErroDoPortal ? e.message : 'Não foi possível.');
      campoDoCodigo.current?.focus();
    } finally { setOcupado(false); }
  };

  const trocarEmail = () => {
    setFase('email'); setCodigo(''); setErroDoCodigo(null); setAviso(null);
  };

  if (fase === 'email') {
    const travado = travadoPara !== null && travadoPara === endereco;
    return (
      <MolduraDaEntrada>
        <Passos atual={1} />
        <h1>Entrar no portal</h1>
        <p className="entrada-explica">
          Use o e-mail em que você recebe os laudos da Testesolo. Mandamos
          para ele um código de seis dígitos — não há senha para criar.
        </p>

        {aviso && <Notificacao tipo={aviso.tipo}>{aviso.texto}</Notificacao>}

        <form onSubmit={pedir}>
          <label className="campo">
            <span className="rot">E-mail</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.currentTarget.value)}
              placeholder="nome@empresa.com.br"
              autoComplete="email"
              inputMode="email"
              enterKeyHint="send"
              autoFocus
              required
            />
            <span className="dica">
              O mesmo endereço que recebe os laudos por e-mail.
            </span>
          </label>
          <button type="submit" className="botao largo seta"
                  disabled={ocupado || endereco.length < 3 || travado}>
            <span>{ocupado ? 'Enviando…' : 'Receber código'}</span>
            <Seta />
          </button>
        </form>

        <Ajuda>
          Ainda sem acesso? Quem libera o portal é a Testesolo — fale com a
          gente pelo <Telefone />.
        </Ajuda>
      </MolduraDaEntrada>
    );
  }

  return (
    <MolduraDaEntrada>
      <Passos atual={2} />
      <h1>Digite o código</h1>

      <Notificacao tipo="ok">
        Se <strong className="quebra">{endereco}</strong> estiver cadastrado
        para receber relatórios, o código chegou. Ele vale
        por {minutos} minutos.{' '}
        <button type="button" className="link-acao" onClick={trocarEmail}>
          Trocar e-mail
        </button>
      </Notificacao>

      {aviso && <Notificacao tipo={aviso.tipo}>{aviso.texto}</Notificacao>}

      <form onSubmit={confirmar}>
        <label className="campo">
          <span className="rot">Código de seis dígitos</span>
          <span className="campo-caixa">
            <input
              ref={campoDoCodigo}
              type="text"
              className="codigo"
              value={codigo}
              /*
               * `inputMode="numeric"` abre o teclado de números no
               * celular; `autoComplete="one-time-code"` deixa o iOS
               * e o Android oferecerem o código direto da
               * notificação, sem a pessoa trocar de aplicativo.
               */
              inputMode="numeric"
              autoComplete="one-time-code"
              enterKeyHint="go"
              maxLength={6}
              aria-invalid={erroDoCodigo ? true : undefined}
              aria-describedby="entrada-codigo-dica"
              onChange={(e) => {
                setCodigo(e.currentTarget.value.replace(/\D/g, '').slice(0, 6));
                if (erroDoCodigo) setErroDoCodigo(null);
              }}
              autoFocus
              required
            />
            {erroDoCodigo && (
              <svg className="icone-erro" width="20" height="20" viewBox="0 0 24 24"
                   aria-hidden="true">
                <circle cx="12" cy="12" r="10" fill="currentColor" />
                <path className="icone-erro-traco" d="M12 7v6M12 16.6v.2"
                      strokeWidth="2.2" strokeLinecap="round" />
              </svg>
            )}
          </span>
          {erroDoCodigo ? (
            <span id="entrada-codigo-dica" className="dica erro" role="alert">
              {erroDoCodigo}
            </span>
          ) : (
            <span id="entrada-codigo-dica" className="dica">
              No celular, o código costuma aparecer pronto acima do teclado.
            </span>
          )}
        </label>
        <button type="submit" className="botao largo seta"
                disabled={ocupado || codigo.length !== 6}>
          <span>{ocupado ? 'Conferindo…' : 'Entrar'}</span>
          <Seta />
        </button>
      </form>

      <button type="button" className="botao fantasma"
              onClick={() => { void pedir(); }}
              disabled={ocupado || restante > 0}>
        {restante > 0
          ? <span>Reenviar em <span className="mono">{relogio(restante)}</span></span>
          : 'Reenviar código'}
      </button>

      <Ajuda>
        Não chegou? Confira a caixa de spam ou ligue para a
        Testesolo: <Telefone />.
      </Ajuda>
    </MolduraDaEntrada>
  );
}
